import { put, select, takeLatest } from 'redux-saga/effects';
import { differenceInSeconds } from 'date-fns';
import fp from 'lodash/fp';

import { ActionPayload, augmentSaga } from 'Modules/core/redux';
import { messagingActions, withErrorHandler } from 'Modules/core/messaging';
import { apiCall } from 'Modules/core/api';

import { feedbackActions } from './actions';
import { FeedbackReducer, SendCode, SendFeedbackForm, SubmitFeedbackForm, SendedConfirmationTimeKey } from './types';
import { FeedbackModalComponentProps, FeedbackModalStage } from '../../components/FeedbackModal/types';
import { ConfirmationCodeRequest, FeedbackRequest, feedback } from '../../api';
import { SENDED_CONFIRMATION_TIME_PATH, SEND_CODE_DELAY_SECONDS } from './constanst';
import { FormValues, Role } from '../../components/FeedbackForm/types';


function* setBusy(busy: boolean) {
    yield put(feedbackActions.setBusy(busy));
}

export const withBusyWrapper = next =>
    function* busyWrapper() {
        yield setBusy(true);
        yield next();
        yield setBusy(false);
    };

function* getEmailCodeSendedTime(email: SendedConfirmationTimeKey) {
    const sendedConfirmations: FeedbackReducer['sendedConfirmationTime'] =
        yield select(fp.getOr({}, `${SENDED_CONFIRMATION_TIME_PATH}`));
    return sendedConfirmations[email];
}

function* sendCodeRequest(email: SendedConfirmationTimeKey) {
    const currentTime = new Date().getTime();
    const sendedTime: number = yield getEmailCodeSendedTime(email);

    if (differenceInSeconds(currentTime, sendedTime) > SEND_CODE_DELAY_SECONDS || !sendedTime) {
        yield put(feedbackActions.setCodeSendTime({ email, time: currentTime }));
        yield apiCall(feedback.sendCode, {
            body: {
                email,
            } as ConfirmationCodeRequest,
        } as Parameters<typeof feedback.sendCode>[0]);
    }
}

function* submitForm({ $payload: { formValues, modalId } }: ActionPayload<SubmitFeedbackForm>) {
    yield sendCodeRequest(formValues.email);

    yield put(messagingActions.updateModalProps({
        id: modalId,
        props: {
            formValues,
            stage: FeedbackModalStage.CONFIRMATION,
            isError: false,
            errorMessage: null,
        } as FeedbackModalComponentProps,
    }));
}

function* sendCode({ $payload: { email } }: ActionPayload<SendCode>) {
    yield sendCodeRequest(email);
}

const formValuesToApiMap = ({
    email, inn, investorStatus, investorType, name, price, rate, role,
}: FormValues): Omit<FeedbackRequest, 'ConfirmationCode'> => ({
    Email: email,
    INN: inn,
    Name: name,
    Role: role,
    ...role === Role.INVESTOR && {
        InvestorStatus: investorStatus,
        InvestorType: investorType,
        PlacementAmount: +price,
        ProfitabilityRate: rate,
    },
    ...role === Role.ISSUER && {
        AttractionAmount: +price,
        AttractionAmountCost: rate,
    },
});

function* sendForm({
    $payload: { ConfirmationCode, formValues, modalId },
}: ActionPayload<SendFeedbackForm>) {
    try {
        yield put(messagingActions.updateModalProps({
            id: modalId,
            props: {
                errorMessage: null,
                isError: false,
            } as FeedbackModalComponentProps,
        }));

        yield apiCall(feedback.sendForm, {
            body: {
                ConfirmationCode,
                ...formValuesToApiMap(formValues),
            } as FeedbackRequest,
        } as Parameters<typeof feedback.sendForm>[0]);

        yield put(messagingActions.updateModalProps({
            id: modalId,
            props: {
                stage: FeedbackModalStage.FINAL,
            } as FeedbackModalComponentProps,
        }));
    } catch (error) {
        yield put(messagingActions.updateModalProps({
            id: modalId,
            props: {
                stage: fp.get('response.status', error) === 400
                    ? FeedbackModalStage.CONFIRMATION : FeedbackModalStage.FINAL,
                errorMessage: fp.get('response.data', error),
                isError: true,
            } as FeedbackModalComponentProps,
        }));
    }
}

export const sagas = [
    takeLatest(`${feedbackActions.submitForm}`, augmentSaga(
        submitForm,
        withBusyWrapper,
        withErrorHandler('feedback.sendFormError'),
    )),
    takeLatest(`${feedbackActions.sendCode}`, augmentSaga(
        sendCode,
        withBusyWrapper,
        withErrorHandler('feedback.sendCodeError'),
    )),
    takeLatest(`${feedbackActions.sendForm}`, augmentSaga(
        sendForm,
        withBusyWrapper,
        withErrorHandler('feedback.sendFormError'),
    )),
];
